import { zodResolver } from "@hookform/resolvers/zod";
import Alert from "@mui/material/Alert";
import type { AlertColor } from "@mui/material/Alert/Alert";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import isEmpty from "lodash/isEmpty";
import isNil from "lodash/isNil";
import omitBy from "lodash/omitBy";
import { useMemo } from "react";
import { useForm, useWatch } from "react-hook-form";

import useQueryClient from "../../../api/query";
import useDevMode from "../../../utils/devmode";
import { stall } from "../../../utils/utils";
import { MASK } from "../../redact";
import {
  CREDENTIAL_TYPE,
  isNewPayer,
  isSupportPayer,
  typeFromCredential,
} from "../common";
import {
  type UpdatePayerCredentialsFormFields,
  updatePayerCredentialsFormSchemaBase,
} from "./PayerCredentialsForm.zod";
import { UpdatePayerCredentialFormFields } from "./UpdatePayerCredentialFormFields";

type UpdateUserCredentialType = {
  updateUserCredential: (credentials: Partial<UserCredentialsMessage>) => void;
};

type GetUserCredentialsType = {
  getUserCredentials: () => void;
};

interface UpdatePayerCredentialsProps {
  credentials: Partial<
    UserCredentialsMessage & UpdateUserCredentialType & GetUserCredentialsType
  >;
  allCredentials: Partial<UserCredentialsMessage>[];
  afterFormSubmit?: () => void;
  updateSnackBar: (snackbar: { severity: AlertColor; message: string }) => void;
  supportedSourcesMap: Record<string, SourceMessage>;
  onClose: () => void;
}

export function UpdatePayerCredentials({
  credentials,
  allCredentials,
  afterFormSubmit = undefined,
  updateSnackBar,
  supportedSourcesMap,
  onClose,
}: UpdatePayerCredentialsProps) {
  const {
    control,
    formState: { errors, isSubmitting, isValid },
    handleSubmit,
    setValue,
  } = useForm<UpdatePayerCredentialsFormFields>({
    mode: "onChange",
    reValidateMode: "onChange",
    delayError: 1000,
    resolver: zodResolver(updatePayerCredentialsFormSchemaBase),
    defaultValues: {
      userType:
        credentials.userType as UpdatePayerCredentialsFormFields["userType"],
      userId: credentials.userId ?? undefined,
    },
  });
  const queryClient = useQueryClient();
  const devMode = useDevMode();
  const { wieldyId: credentialId } = credentials;
  const credentialType = typeFromCredential(credentials);

  // Watch the user fields so the alert reacts to changes in the form
  const watchedUserType = useWatch({ control, name: "userType" });
  const watchedUserId = useWatch({ control, name: "userId" });
  const currentUserType = watchedUserType ?? credentials.userType;
  const currentUserId = watchedUserId ?? credentials.userId;

  // Other agents = credentials for the same practice+payer/PMS but a different user
  const otherAgents = useMemo(
    () =>
      allCredentials.filter(
        (c) =>
          c.wieldyId !== credentialId &&
          c.practiceId === credentials.practiceId &&
          (credentialType === CREDENTIAL_TYPE.PMS
            ? c.pmsDisplayName === credentials.pmsDisplayName
            : c.sourceId === credentials.sourceId),
      ),
    [allCredentials, credentialId, credentials, credentialType],
  );

  const onUpdate = async ({
    username,
    password,
    website,
    notes,
    mfaJson,
    activationCode,
    serialNumber,
    customerNumber,
    machineIdentifier,
    userType,
    userId,
  }: Omit<UpdatePayerCredentialsFormFields, "confirmPassword">) => {
    // Duplicate check: if userType/userId changed, ensure the new combination doesn't already exist
    const duplicate = allCredentials.find(
      (c) =>
        c.wieldyId !== credentialId &&
        c.practiceId === credentials.practiceId &&
        c.userType === userType &&
        c.userId === userId &&
        (credentialType === CREDENTIAL_TYPE.PMS
          ? c.pmsDisplayName === credentials.pmsDisplayName
          : c.sourceId === credentials.sourceId),
    );

    if (duplicate) {
      updateSnackBar({
        severity: "warning",
        message:
          `A credential for ${userType}: ${userId} already exists for this ` +
          `${credentialType === CREDENTIAL_TYPE.PMS ? "PMS" : "payer"}. ` +
          `Please update that credential instead.`,
      });
      return;
    }

    try {
      const body = omitBy(
        {
          username,
          password,
          website,
          notes,
          mfaJson:
            devMode && credentials.mfaJson !== MASK
              ? mfaJson
              : credentials.mfaJson,
          activationCode,
          serialNumber,
          customerNumber,
          machineIdentifier,
          userType,
          userId,
        },
        (value) => isNil(value) || value === MASK,
      );

      if (credentialId && !isEmpty(body)) {
        if (credentialType === CREDENTIAL_TYPE.PAYER) {
          if (isSupportPayer(credentials)) {
            await Promise.all([
              await queryClient.updateCreateCredentials({
                credentialId,
                ...body,
              }),
              await stall(),
            ]);
          } else if (isNewPayer(credentials)) {
            await Promise.all([
              await queryClient.updateOtherCredentials({
                credentialId,
                ...body,
              }),
              await stall(),
            ]);
          }
        }
        if (credentialType === CREDENTIAL_TYPE.PMS) {
          await Promise.all([
            await queryClient.updatePMSCredentials({
              credentialId,
              ...body,
            }),
            await stall(),
          ]);
        }
        const { updateUserCredential, getUserCredentials } = credentials;

        if (
          typeof updateUserCredential === "function" &&
          typeof getUserCredentials === "function"
        ) {
          updateUserCredential(body);
          getUserCredentials();
        }

        if (typeof afterFormSubmit === "function") {
          afterFormSubmit();
        }
        updateSnackBar({
          severity: "success",
          message: "Successfully updated credentials.",
        });
      }
    } catch (error) {
      updateSnackBar({
        severity: "error",
        message: "Failed to update credentials. Try again.",
      });
      // biome-ignore lint/suspicious/noConsole: intentional browser console output
      console.error(`Failed to update credentials: ${error}`);
    }
  };

  const hasErrors = Object.keys(errors).length > 0;
  const isDisabled = isSubmitting || hasErrors || !isValid;

  return (
    <Box
      component="form"
      onSubmit={handleSubmit(onUpdate)}
      sx={{
        display: "flex",
        flexDirection: "column",
      }}
    >
      <UpdatePayerCredentialFormFields
        control={control}
        setValue={setValue}
        currentCredentials={credentials}
        supportedSourcesMap={supportedSourcesMap}
      />
      {otherAgents.length > 0 && (
        <Alert severity="warning" sx={{ marginBottom: "12px" }}>
          Also update the other agent(s) for this{" "}
          {credentialType === CREDENTIAL_TYPE.PMS ? "PMS" : "payer"} + practice
          combination:{" "}
          {otherAgents.map((c) => `${c.userType}: ${c.userId}`).join(", ")}.
          Current: {currentUserType}: {currentUserId}.
        </Alert>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          gap: "12px",
        }}
      >
        <Button
          fullWidth
          sx={{ borderRadius: "8px" }}
          onClick={onClose}
          color="primary"
          data-testid="cancel"
        >
          Cancel
        </Button>
        <Button
          fullWidth
          sx={{ borderRadius: "8px" }}
          type="submit"
          autoFocus
          color="primary"
          variant="contained"
          disabled={isDisabled}
          data-testid="updateCredentials"
        >
          {isSubmitting ? (
            <Box
              sx={{
                display: "flex",
                flexDirection: "row",
                alignItems: "center",
                gap: "4px",
              }}
            >
              <CircularProgress size={20} />
              Submitting...
            </Box>
          ) : (
            "Save"
          )}
        </Button>
      </Box>
    </Box>
  );
}
